// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// linkPreview generates inline previews for URLs in a message — CLIENT-SIDE only, so a
// homeserver never sees what you link (E2E-safe). The browser's same-origin policy blocks
// reading arbitrary pages' OpenGraph tags (CORS), so we preview only what works WITHOUT a
// page fetch: direct media URLs (rendered straight) and a few providers with a predictable
// public thumbnail (YouTube). Everything else just stays a plain link (markdown already
// autolinks it). Off by DEFAULT — enabling it loads remote media, which reveals your IP to
// that host.
const PREVIEW_KEY = "zalmoxis.linkpreviews";

export function linkPreviewsEnabled(): boolean {
  return localStorage.getItem(PREVIEW_KEY) === "on";
}
export function setLinkPreviews(on: boolean): void {
  localStorage.setItem(PREVIEW_KEY, on ? "on" : "off");
}

export type Preview =
  | { kind: "image" | "video" | "audio"; url: string }
  | { kind: "youtube"; url: string; videoID: string; thumb: string };

const URL_RE = /https?:\/\/[^\s<>")]+/i;
const IMAGE_RE = /\.(?:png|jpe?g|gif|webp|avif|bmp|svg)(?:\?[^\s]*)?$/i;
const VIDEO_RE = /\.(?:mp4|webm|mov|m4v)(?:\?[^\s]*)?$/i;
const AUDIO_RE = /\.(?:mp3|ogg|oga|wav|m4a|flac)(?:\?[^\s]*)?$/i;

// youtubeID extracts a YouTube video id from watch / youtu.be / shorts / embed URLs.
function youtubeID(u: URL): string | null {
  const host = u.hostname.replace(/^www\./, "");
  if (host === "youtu.be") return u.pathname.slice(1).split("/")[0] || null;
  if (host === "youtube.com" || host === "m.youtube.com") {
    if (u.pathname === "/watch") return u.searchParams.get("v");
    const sm = /^\/(?:shorts|embed)\/([^/?]+)/.exec(u.pathname);
    if (sm) return sm[1];
  }
  return null;
}

// firstPreview returns a renderable preview for the FIRST previewable URL in text, or null.
export function firstPreview(text: string): Preview | null {
  const m = URL_RE.exec(text);
  if (!m) return null;
  const raw = m[0].replace(/[.,);]+$/, ""); // trim trailing punctuation
  let u: URL;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  const yt = youtubeID(u);
  if (yt) return { kind: "youtube", url: raw, videoID: yt, thumb: `https://i.ytimg.com/vi/${yt}/hqdefault.jpg` };
  if (IMAGE_RE.test(u.pathname)) return { kind: "image", url: raw };
  if (VIDEO_RE.test(u.pathname)) return { kind: "video", url: raw };
  if (AUDIO_RE.test(u.pathname)) return { kind: "audio", url: raw };
  return null;
}
